<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { BellPlusIcon, BellRingIcon } from '@lucide/vue';
import AppModal from '@/components/v2/AppModal.vue';
import DashboardSubscriptionForm from '@/components/subscriptions/DashboardSubscriptionForm.vue';
import { useToast } from '@/composables/useToast';
import { useUser } from '@/composables/useUser';
import { dashboardSubscriptionService, isConflictError } from '@/services/DashboardSubscriptionService';
import { DashboardSubscription, DashboardSubscriptionRequest, SubscribableDashboard } from '@/types/DashboardSubscription';
import { NotificationUserRoles } from '@/types/UserRole';
import { isDashboardSubscriptionsEnabled } from '@/utilities/FeatureFlags';

const props = defineProps<{
  dashboardId: string;
}>();

const toast = useToast();
const { role } = useUser();

const dashboard = ref<SubscribableDashboard | null>(null);
const subscription = ref<DashboardSubscription | null>(null);
const showModal = ref(false);

const canSubscribe = computed(() => isDashboardSubscriptionsEnabled() && NotificationUserRoles.includes(role.value));
const subscribed = computed(() => !!dashboard.value?.subscriptionId);
const formDashboards = computed(() => (dashboard.value ? [dashboard.value] : []));

async function loadDashboard() {
  const requestedId = props.dashboardId;
  dashboard.value = null;
  if (!canSubscribe.value) {
    return;
  }
  try {
    const loaded = await dashboardSubscriptionService.dashboard(requestedId);
    // A slower answer for the dashboard the user just left must not label this one.
    if (requestedId === props.dashboardId) {
      dashboard.value = loaded;
    }
  } catch {
    // The button simply stays hidden: subscribing is secondary to viewing the dashboard.
  }
}

async function open() {
  subscription.value = null;
  if (subscribed.value) {
    try {
      const subscriptions = await dashboardSubscriptionService.list();
      subscription.value = subscriptions.find((s) => s.id === dashboard.value?.subscriptionId) ?? null;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Your subscription could not be loaded.');
      return;
    }
    if (!subscription.value) {
      toast.info('That subscription has been removed. You can subscribe again.');
      await loadDashboard();
      return;
    }
  }
  showModal.value = true;
}

async function save(request: DashboardSubscriptionRequest) {
  try {
    if (subscription.value) {
      await dashboardSubscriptionService.update(subscription.value.id, request);
    } else {
      await dashboardSubscriptionService.create(request);
    }
  } catch (error) {
    if (isConflictError(error)) {
      await loadDashboard();
      throw new Error('You already subscribe to this dashboard. Close this and choose Subscribed to edit it.');
    }
    throw error;
  }
  toast.success(subscription.value ? 'Subscription updated.' : 'Subscribed. Manage it under Notifications.');
  showModal.value = false;
  await loadDashboard();
}

watch(() => props.dashboardId, loadDashboard, { immediate: true });
</script>

<template>
  <template v-if="dashboard">
    <button
      class="fixed right-16 bottom-4 z-50 flex items-center gap-1.5 rounded-full bg-white px-3 py-2 text-sm shadow-lg transition-colors hover:bg-gray-100"
      :title="subscribed ? 'Edit your email subscription' : 'Get this dashboard by email'"
      data-testid="dashboard-subscribe"
      @click="open">
      <component
        :is="subscribed ? BellRingIcon : BellPlusIcon"
        class="h-5 w-5" />
      {{ subscribed ? 'Subscribed' : 'Subscribe' }}
    </button>

    <AppModal
      :show="showModal"
      :title="subscribed ? 'Edit subscription' : `Subscribe to ${dashboard.name}`"
      :handle-cancel="() => (showModal = false)"
      size="xl">
      <template #content>
        <DashboardSubscriptionForm
          v-if="showModal"
          :dashboards="formDashboards"
          :subscription="subscription"
          :initial-dashboard-id="dashboard.accountTableauId"
          :save="save"
          @cancel="showModal = false" />
      </template>
      <template #actions>
        <span />
      </template>
    </AppModal>
  </template>
</template>
